import { Box, Stack } from '@mui/material';
import type { FixedDynamicLineItem } from '@vy/dynamic-invoice-builder';
import { formatPrice } from '@vy/services';
import { PRICING_MODELS, type Currency } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynamicInvoiceContentSelectors, dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { useDynamicInvoiceIds } from '../../hooks/use-dynamic-invoice-ids';
import type { ModifyLineItemData } from '../types';
import { getLineItemPercentage } from '../utils/get-line-item-percentage';
import { LineItemHeader } from './LineItemHeader';
import { MetricBox } from './MetricBox';
import { ModifyUsageLineItemSection } from './ModifyUsageLineItemSection';
import { lineItemActionStyles, usageItemAccordionStyles } from './styles';
import { defined } from '@vy/null';

type FixedFeeItemProps = {
  lineItem: FixedDynamicLineItem;
  currency: Currency;
};

export const FixedFeeItem: FC<FixedFeeItemProps> = ({ lineItem, currency }) => {
  const navigate = useNavigate();
  const { invoiceId, customerId, reviewStatus } = useDynamicInvoiceIds();

  const shouldShowMenu = reviewStatus !== 'Approved';
  const isDisabled = lineItem.disabled;
  const childInvoice = lineItem.originalInvoiceData;
  const isClickable = isNotNullOrUndefined(childInvoice);
  const percentage = getLineItemPercentage(lineItem);

  const isSubscription = lineItem.pricingModel === PRICING_MODELS.Subscription;
  const subscriptionSeats = isSubscription ? lineItem.priceConfiguration.seats : undefined;

  const hasSeats = defined(subscriptionSeats) && subscriptionSeats > 1;
  const subscriptionPrice = isSubscription ? lineItem.priceConfiguration.price : undefined;

  const lineItemData: ModifyLineItemData = {
    type: 'Fixed',
    name: lineItem.name,
    description: lineItem.description,
    price: lineItem.totalAmount,
    modifiedName: null,
    modifiedDescription: null,
    modifiedPrice: null,
  };

  const handleClick = () => {
    if (!childInvoice) {
      return;
    }

    navigate(`/invoices/${childInvoice.id}/customer/${childInvoice.customerId}/dynamic`);
  };
  return (
    <Box
      onClick={isClickable ? handleClick : undefined}
      sx={{
        ...usageItemAccordionStyles.accordion,
        padding: '16px 24px',
        opacity: isDisabled ? 0.5 : 1,
        cursor: isClickable ? 'pointer' : 'default',
      }}
      {...getSelectorAttribute(dynamicInvoiceContentSelectors.fixedFeeLineItem)}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%"
        }}>
        <LineItemHeader
          name={lineItem.name}
          description={lineItem.description}
          percentage={percentage}
        />

        <Stack
          direction="row"
          sx={[{
            alignItems: "center"
          }, lineItemActionStyles.actionsContainer]}>
          <Stack
            direction="row"
            sx={[{
              alignItems: "center"
            }, lineItemActionStyles.metricsContainer]}>
            {isNotNullOrUndefined(lineItem.basePrice) && (
              <Box {...getSelectorAttribute(dynamicInvoicePageSelectors.productLineItem.baseAmount)}>
                <MetricBox label="Base amount" value={formatPrice(lineItem.basePrice, currency)} />
              </Box>
            )}
            {hasSeats && (
              <>
                <MetricBox label="Price" value={formatPrice(subscriptionPrice ?? 0, currency)} />
                <MetricBox label="Seats" value={String(subscriptionSeats)} />
              </>
            )}
            <Box {...getSelectorAttribute(dynamicInvoicePageSelectors.productLineItem.amount)}>
              <MetricBox label="Amount" value={formatPrice(lineItem.totalAmount, currency)} />
            </Box>
          </Stack>
          {shouldShowMenu && (
            <ModifyUsageLineItemSection
              lineItemId={lineItem.lineItemId}
              customerId={customerId}
              invoiceId={invoiceId}
              lineItemData={lineItemData}
              disabled={isDisabled}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
